import Loading from 'common/components/Elements/Loading/Loading';
import {
  buildFileName, downloadImgFile,
} from 'common/utilities/browser/downloadFile/downloadFile';
import type {ImagePart } from 'common/utilities/images/images';
import { combineImages } from 'common/utilities/images/images';
import { sleep } from 'common/utilities/timeout/timeout';
import { enqueueWarningNotification } from 'core/redux/actions/notification/notificationActions';
import { useAppDispatch } from 'core/redux/store/configureStore';
import * as htmlToImage from 'html-to-image';
import I18n from 'i18n/en-US';
import React, { useEffect, useRef } from 'react';
import type { SequenceAlign, SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { changeExportToPNGStatus } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';

async function convertSequenceTableRulerToImage() {
  const container = document.body.querySelector('#sequenceTableRuler') as HTMLDivElement;
  const svgEl = container.querySelector('svg') as unknown as HTMLElement;
  const xOffset = (container.firstChild as HTMLDivElement).offsetWidth;

  const image: ImagePart = {
    data: await htmlToImage.toBlob(svgEl, { pixelRatio: 1 }) as Blob, y: 0, x: xOffset,
  };

  const { height } = container.getBoundingClientRect();

  return { image, height };
}

export function exportToPNGError(alignedSequences: SequenceAlign[]) {
  // Limit the max amount of sequences a user can export to PNG to avoid crashes.
  // Around 50 000 residues (i.e. 100 sequences of 500)
  const maxSequencesToExportToPNG = alignedSequences.length > 0
    ? Math.round(50000 / alignedSequences[0].residues.length)
    : 0;

  if (maxSequencesToExportToPNG < alignedSequences.length) {
    return I18n.SequenceAnalyser.TableActions.ExportToPNG.errorTooMany(maxSequencesToExportToPNG);
  }

  // Export to SVG is somehow scaled down for too long elements.
  // Workaround is to limit the length of sequences for now.
  const maxResiduesCount = 850;
  if (alignedSequences.some((s) => s.residues.length > maxResiduesCount)) {
    return I18n.SequenceAnalyser.TableActions.ExportToPNG.errorTooLong(maxResiduesCount);
  }

  return undefined;
}

/**
 * All chunks and residues chunks are marked as rendered or not. Loop until all are rendered when
 * exporting to PNG as it might take several seconds when exporting large table to render everything.
 */
async function exportToPNG(sequenceType: SequenceType, maxRetry: number, currentRetry: number) {
  if (currentRetry > maxRetry) return false;

  const areAllChunksVisible = document.querySelectorAll('[data-chunk-rendered="false"]').length === 0;

  if (!areAllChunksVisible) {
    await sleep(1000);
    return exportToPNG(sequenceType, maxRetry, currentRetry + 1);
  }

  try {
    const el = document.querySelector('#sequences') as HTMLDivElement | undefined;

    if (el) {
      el.style.background = 'white';

      let sequencesWidth = 0;
      let sequencesHeight = 0;

      const { image: rulerImage, height: rulerHeight } = await convertSequenceTableRulerToImage();

      // htmlToImage export as follow when using toPng: HTML > SVG (using foreignObject) > Canvas > PNG
      // It seems to fail on Firefox when trying to load the SVG base64 data to an Image element.
      // To circumvent that browser issue, sequences are exported line per line and then only combined
      // together on one canvas element to be exported as PNG.
      const images: ImagePart[] = [rulerImage];
      let y = rulerHeight;
      sequencesHeight += rulerHeight;

      for (const seqEl of el.querySelectorAll('.sequence, .sequenceFeature')) {
        images.push({ data: await htmlToImage.toBlob(seqEl as HTMLElement, { pixelRatio: 1 }) as Blob, y, x: 0 });
        const { width, height } = seqEl.getBoundingClientRect();
        y += height;
        sequencesHeight += height;
        if (sequencesWidth === 0) sequencesWidth = width;
      }

      const dataUrl = await combineImages(sequencesWidth, sequencesHeight, images);

      const fileName = buildFileName(
        sequenceType === 'protein' ? 'ProteinSequences' : 'NucleotideSequences', 'png',
      );

      if (dataUrl) downloadImgFile(fileName, dataUrl);

      el.style.removeProperty('background');

      return true;
    }
  // eslint-disable-next-line no-empty
  } catch (e) { }

  return false;
}

interface ComponentProps {
  sequenceType: SequenceType;
}

function ExportToPNG({ sequenceType }: ComponentProps) {
  const dispatch = useAppDispatch();
  const waiting = useRef(false);

  const process = async () => {
    if (waiting.current) return;

    waiting.current = true;

    // Let the loader render first as some export take some resources / time
    await sleep(200);

    dispatch(changeExportToPNGStatus('processing'));

    const successful = await exportToPNG(sequenceType, 120, 0);
    if (!successful) {
      dispatch(enqueueWarningNotification(
        I18n.SequenceAnalyser.TableActions.ExportToPNG.loadingError,
      ));
    }

    dispatch(changeExportToPNGStatus(undefined));

    waiting.current = false;
  };

  useEffect(() => {
    process();
  });

  return (
    <Loading inBackdrop label={I18n.SequenceAnalyser.TableActions.ExportToPNG.loading} hideSeconds />
  );
}
export default ExportToPNG;
